import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  approveAuthor, approveItem, approveList, approveSite,
  rejectAuthor, rejectItem, rejectList, rejectSite,
} from '../../../redux/features/pending/pendingSlice';
import { setSnackbar } from '../../../redux/features/snackbarSlice';
import { verdictResultMessage } from '../../../common/VerdictMessages';
import useGoBack from '../../../hooks/useGoBack';

const VERDICT_THUNKS = {
  item: { approve: approveItem, reject: rejectItem },
  list: { approve: approveList, reject: rejectList },
  author: { approve: approveAuthor, reject: rejectAuthor },
  site: { approve: approveSite, reject: rejectSite },
};

/**
 * A hero page's verdict on the pending entity it is showing. The decision
 * consumes the pending copy, it leaves the page rather than refetching a
 * record that is now gone; the queue behind it re-fetches on its own. `name`
 * titles the confirmation, the only feedback left once the page is gone.
 */
export default function useVerdict(entity, id, name) {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const [deciding, setDeciding] = useState(false);

  const decide = useCallback((kind, reason = null) => {
    setDeciding(true);
    dispatch(VERDICT_THUNKS[entity][kind]({ id, reason }))
      .unwrap()
      .then(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: verdictResultMessage(kind, entity, name, reason),
        }));
        goBack();
      })
      .catch(() => {
        setDeciding(false);
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to ${kind} ${entity}.`,
        }));
      });
  }, [dispatch, entity, goBack, id, name]);

  return { decide, deciding };
}
